/**
 * Categories (Classic) — sound palette.
 *
 * Studio-presentation timbre. Sine + triangle bells, warm pad swells, a
 * synthesized non-infringing "think music" bed during clue display.
 *
 * Every play* routes through `audio.playIfNotMuted` so the global mute toggle
 * works automatically.
 */

import { audio } from '@/juice';

import type { HapticStyle } from '@rendever/games-contracts';

export type BoardSoundName =
  | 'tileSelect'
  | 'clueReveal'
  | 'dailyDouble'
  | 'correct'
  | 'incorrect'
  | 'wagerTick'
  | 'submit'
  | 'roundEnd'
  | 'nav'
  | 'tap';

export const soundHaptic: Partial<Record<BoardSoundName, HapticStyle>> = {
  tileSelect: 'light',
  clueReveal: 'light',
  dailyDouble: 'success',
  correct: 'success',
  incorrect: 'light',
  wagerTick: 'light',
  submit: 'medium',
  roundEnd: 'success',
  nav: 'light',
  tap: 'light',
};

// ---------------------------------------------------------------------------
// Tile select — brief warm sine tone, 880 Hz, gentle envelope
// ---------------------------------------------------------------------------

function synthTileSelect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 880, type: 'sine' });
  // Slight pitch jitter ±15 cents.
  osc.detune.value = (Math.random() - 0.5) * 30;
  const { node: g } = audio.envelope(ctx, {
    attack: 0.004,
    decay: 0.04,
    sustain: 0.3,
    release: 0.18,
    peak: 0.22,
  });
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.32);

  // Light click layer for tactile feel.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 24 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const hp = ctx.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.value = 2400;
  const { node: ng } = audio.envelope(ctx, {
    attack: 0.001,
    release: 0.03,
    peak: 0.1,
  });
  audio.connectThrough(src, hp, ng, master);
  src.start(t);
  src.stop(t + 0.04);
}

// ---------------------------------------------------------------------------
// Clue reveal — ascending three-note (C5 → E5 → G5), brief, confident
// ---------------------------------------------------------------------------

function synthClueReveal(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const notes = [523.25, 659.25, 783.99]; // C5, E5, G5
  notes.forEach((freq, i) => {
    const delay = i * 0.08;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.16, t + delay + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.55);
    audio.connectThrough(osc, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.6);
  });
}

// ---------------------------------------------------------------------------
// Daily Double — two-note ascending flourish (E5 → A5) + warm pad swell
// ---------------------------------------------------------------------------

function synthDailyDouble(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;

  // Two ascending notes — saw + low-pass for warm horn-pad timbre.
  const notes = [
    { freq: 659.25, delay: 0.0 }, // E5
    { freq: 880.0, delay: 0.18 }, // A5
  ];
  notes.forEach(({ freq, delay }) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sawtooth' });
    osc.detune.value = (Math.random() - 0.5) * 24;
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1600;
    lp.Q.value = 1.5;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.18, t + delay + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.7);
    audio.connectThrough(osc, lp, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.8);
  });

  // Warm pad swell underneath — sine at A3 + E4 (root + fifth).
  [220, 329.63].forEach((freq) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    audio.connectThrough(osc, g, master);
    osc.start(t);
    osc.stop(t + 1.5);
  });

  // Sparkle — tiny high triangle motes for the "bloom" moment.
  for (let i = 0; i < 4; i += 1) {
    const delay = 0.05 + i * 0.07;
    const osc = audio.makeOscillator(ctx, {
      freq: 1760 + i * 220,
      type: 'triangle',
    });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.04, t + delay + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.25);
    audio.connectThrough(osc, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.3);
  }
}

// ---------------------------------------------------------------------------
// Correct — ascending major third (E5 → G#5) + brief sparkle
// ---------------------------------------------------------------------------

function synthCorrect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const notes = [659.25, 830.61]; // E5, G#5
  notes.forEach((freq, i) => {
    const delay = i * 0.1;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.18, t + delay + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.6);
    audio.connectThrough(osc, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.65);
  });

  // Tiny sparkle layer — single triangle harmonic.
  const sparkle = audio.makeOscillator(ctx, { freq: 1976, type: 'triangle' });
  const sg = ctx.createGain();
  sg.gain.setValueAtTime(0.0001, t + 0.04);
  sg.gain.exponentialRampToValueAtTime(0.05, t + 0.06);
  sg.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
  audio.connectThrough(sparkle, sg, master);
  sparkle.start(t + 0.04);
  sparkle.stop(t + 0.4);
}

// ---------------------------------------------------------------------------
// Incorrect — descending minor third (A4 → F4), soft envelope. NOT a buzzer.
// ---------------------------------------------------------------------------

function synthIncorrect(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const notes = [440, 349.23]; // A4, F4
  notes.forEach((freq, i) => {
    const delay = i * 0.16;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.14, t + delay + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.45);
    audio.connectThrough(osc, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.5);
  });
}

// ---------------------------------------------------------------------------
// Wager tick — sharp short pip (used when adjusting wager amount).
// ---------------------------------------------------------------------------

function synthWagerTick(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 1320, type: 'triangle' });
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.08, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.08);
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.1);
}

// ---------------------------------------------------------------------------
// Submit — confident downward stamp (G4 → C4)
// ---------------------------------------------------------------------------

function synthSubmit(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const notes = [392, 261.63]; // G4, C4
  notes.forEach((freq, i) => {
    const delay = i * 0.07;
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + delay);
    g.gain.exponentialRampToValueAtTime(0.16, t + delay + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + delay + 0.32);
    audio.connectThrough(osc, g, master);
    osc.start(t + delay);
    osc.stop(t + delay + 0.35);
  });
}

// ---------------------------------------------------------------------------
// Round end — warm sustained chord (C major in mid register)
// ---------------------------------------------------------------------------

function synthRoundEnd(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const chord = [261.63, 329.63, 392, 523.25]; // C4 E4 G4 C5
  chord.forEach((freq, i) => {
    const osc = audio.makeOscillator(ctx, { freq, type: 'sine' });
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t + i * 0.04);
    g.gain.exponentialRampToValueAtTime(0.11, t + i * 0.04 + 0.25);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
    audio.connectThrough(osc, g, master);
    osc.start(t + i * 0.04);
    osc.stop(t + 1.5);
  });

  // Soft cymbal swell — filtered noise.
  const buf = audio.noiseBuffer(ctx, { lengthMs: 1500, shaping: 1.2 });
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 6000;
  bp.Q.value = 1;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.05, t + 0.6);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 1.4);
  audio.connectThrough(src, bp, g, master);
  src.start(t);
  src.stop(t + 1.45);
}

// ---------------------------------------------------------------------------
// Nav — neutral soft click for back / forward navigation
// ---------------------------------------------------------------------------

function synthNav(): void {
  const ctx = audio.getAudioContext();
  const master = audio.getMasterGain();
  if (!ctx || !master) return;

  const t = ctx.currentTime;
  const osc = audio.makeOscillator(ctx, { freq: 660, type: 'triangle' });
  osc.frequency.exponentialRampToValueAtTime(440, t + 0.1);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.12, t + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t + 0.14);
  audio.connectThrough(osc, g, master);
  osc.start(t);
  osc.stop(t + 0.18);
}

// ---------------------------------------------------------------------------
// Think music — synthesized non-infringing background bed
// ---------------------------------------------------------------------------

/**
 * Think-music synth — 4-bar loop at 126 BPM in F major, I-vi-IV-V progression.
 *
 * Structure per beat: walking quarter-note bass, Rhodes-y triangle pad on
 * chord tones, brushed-noise "snare" on beats 2 and 4. No melody lead — the
 * bed is a clock dressed up as a tune, per Research B Section 3.
 *
 * Loops by scheduling 4 bars at a time. Caller starts/stops with the
 * public `startThinkMusic` / `stopThinkMusic` controls.
 */
class ThinkMusic {
  private active = false;
  private timer: number | null = null;

  start(): void {
    if (this.active) return;
    const ctx = audio.getAudioContext();
    if (!ctx) return;
    this.active = true;
    this.scheduleLoop();
  }

  stop(): void {
    this.active = false;
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private scheduleLoop(): void {
    if (!this.active) return;
    // Re-check the global mute gate each loop. The bed schedules ~4-bar
    // chunks; if the player muted mid-bed via the PreferencesBar, we must
    // self-stop so the next chunk doesn't render. POLISH-GATE #9 — every
    // continuous audio source has to respect mute at its scheduling cadence,
    // not only at start time. (Mirrors Balloons' `BalloonMusic.ts` polling
    // pattern.)
    if (audio.isMuted()) {
      this.active = false;
      if (this.timer !== null) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
      return;
    }
    const ctx = audio.getAudioContext();
    const master = audio.getMasterGain();
    if (!ctx || !master) return;

    const bpm = 126;
    const beat = 60 / bpm; // seconds per beat
    // 4 bars * 4 beats = 16 beats
    const loopDurSec = 16 * beat;
    const t0 = ctx.currentTime + 0.05;

    // Per-instrument bus so we can keep the bed quiet.
    const bedGain = ctx.createGain();
    bedGain.gain.value = 0.5;
    bedGain.connect(master);

    // Chord progression: I, vi, IV, V in F major.
    // F major = F A C, D minor = D F A, B♭ major = B♭ D F, C major = C E G.
    // Held one bar each.
    const chords: Array<[number, number, number]> = [
      [174.61, 220.0, 261.63], // F3 A3 C4
      [146.83, 174.61, 220.0], // D3 F3 A3
      [116.54, 174.61, 233.08], // Bb2 F3 Bb3
      [130.81, 164.81, 196.0], // C3 E3 G3
    ];
    // Walking bass note per beat — root, third, fifth, sixth per chord.
    const bassPerBar: number[][] = [
      [87.31, 110.0, 130.81, 146.83], // F2 A2 C3 D3
      [73.42, 87.31, 110.0, 130.81], // D2 F2 A2 C3
      [58.27, 73.42, 87.31, 98.0], // Bb1 D2 F2 G2
      [65.41, 82.41, 98.0, 110.0], // C2 E2 G2 A2
    ];

    for (let bar = 0; bar < 4; bar += 1) {
      const barStart = t0 + bar * 4 * beat;
      const chord = chords[bar]!;
      const bassNotes = bassPerBar[bar]!;

      // Chord pad — held for the whole bar with soft envelope.
      chord.forEach((freq, ci) => {
        const osc = ctx.createOscillator();
        osc.type = 'triangle';
        osc.frequency.value = freq;
        const padG = ctx.createGain();
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 1400;
        lp.Q.value = 0.7;
        padG.gain.setValueAtTime(0.0001, barStart);
        padG.gain.exponentialRampToValueAtTime(
          0.045 + ci * 0.005,
          barStart + 0.2,
        );
        padG.gain.setValueAtTime(0.04 + ci * 0.005, barStart + 4 * beat - 0.3);
        padG.gain.exponentialRampToValueAtTime(0.0001, barStart + 4 * beat - 0.02);
        osc.connect(lp).connect(padG).connect(bedGain);
        osc.start(barStart);
        osc.stop(barStart + 4 * beat + 0.05);
      });

      // Walking bass — one quarter-note per beat.
      bassNotes.forEach((freq, bi) => {
        const beatStart = barStart + bi * beat;
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, beatStart);
        g.gain.exponentialRampToValueAtTime(0.16, beatStart + 0.015);
        g.gain.exponentialRampToValueAtTime(0.0001, beatStart + beat * 0.9);
        osc.connect(g).connect(bedGain);
        osc.start(beatStart);
        osc.stop(beatStart + beat);
      });

      // Brushed snare on beats 2 and 4.
      for (const beatIdx of [1, 3]) {
        const beatStart = barStart + beatIdx * beat;
        const buf = audio.noiseBuffer(ctx, { lengthMs: 110, shaping: 1.4 });
        const src = ctx.createBufferSource();
        src.buffer = buf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.value = 4800;
        bp.Q.value = 0.9;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, beatStart);
        g.gain.exponentialRampToValueAtTime(0.05, beatStart + 0.01);
        g.gain.exponentialRampToValueAtTime(0.0001, beatStart + 0.1);
        src.connect(bp).connect(g).connect(bedGain);
        src.start(beatStart);
        src.stop(beatStart + 0.12);
      }
    }

    // Schedule next loop ~150ms before this one ends to avoid a gap.
    const nextMs = Math.max(50, (loopDurSec - 0.15) * 1000);
    this.timer = window.setTimeout(() => this.scheduleLoop(), nextMs);
  }
}

const thinkMusic = new ThinkMusic();

// ---------------------------------------------------------------------------
// Public controller
// ---------------------------------------------------------------------------

class BoardSound {
  unlock(): void {
    audio.unlockAudio();
  }

  play(name: BoardSoundName): void {
    audio.playIfNotMuted(name, () => {
      audio.unlockAudio();
      switch (name) {
        case 'tileSelect':
          synthTileSelect();
          return;
        case 'clueReveal':
          synthClueReveal();
          return;
        case 'dailyDouble':
          synthDailyDouble();
          return;
        case 'correct':
          synthCorrect();
          return;
        case 'incorrect':
          synthIncorrect();
          return;
        case 'wagerTick':
          synthWagerTick();
          return;
        case 'submit':
          synthSubmit();
          return;
        case 'roundEnd':
          synthRoundEnd();
          return;
        case 'nav':
          synthNav();
          return;
        case 'tap':
          synthTileSelect();
          return;
      }
    });
  }

  /** Start the think-music bed (idempotent). */
  startThinkMusic(): void {
    audio.playIfNotMuted('thinkMusic', () => {
      audio.unlockAudio();
      thinkMusic.start();
    });
  }

  /** Stop the think-music bed (always safe to call). */
  stopThinkMusic(): void {
    thinkMusic.stop();
  }
}

export const sound = new BoardSound();
